package com.banyan.flightviewer.state

import com.banyan.flightviewer.util.nowMs
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow

class AppState {

    val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    // --- Aircraft data ---
    private val _aircraftList = MutableStateFlow<List<AircraftData>>(emptyList())
    val aircraftList = _aircraftList.asStateFlow()

    private val _selectedCallsign = MutableStateFlow<String?>(null)
    val selectedCallsign = _selectedCallsign.asStateFlow()

    private val _trackedFlightCallsign = MutableStateFlow<String?>(null)
    val trackedFlightCallsign = _trackedFlightCallsign.asStateFlow()

    private val _cachedEnrichedData = MutableStateFlow<Map<String, EnrichedFlightData>>(emptyMap())
    val cachedEnrichedData = _cachedEnrichedData.asStateFlow()

    // --- Settings ---
    private val _settings = MutableStateFlow(AppSettings.default())
    val settings = _settings.asStateFlow()

    // --- Pixoo state ---
    private val _pixooConnected = MutableStateFlow(false)
    val pixooConnected = _pixooConnected.asStateFlow()

    private val _pixooFrameIndex = MutableStateFlow(0)
    val pixooFrameIndex = _pixooFrameIndex.asStateFlow()

    private val _pixooOriginalChannel = MutableStateFlow<Int?>(null)
    val pixooOriginalChannel = _pixooOriginalChannel.asStateFlow()

    private val _pixooStateSaved = MutableStateFlow(false)
    val pixooStateSaved = _pixooStateSaved.asStateFlow()

    private val _pixooCallsign = MutableStateFlow<String?>(null)
    val pixooCallsign = _pixooCallsign.asStateFlow()

    // --- UI state ---
    private val _isMonitoring = MutableStateFlow(false)
    val isMonitoring = _isMonitoring.asStateFlow()

    private val _detailPanelVisible = MutableStateFlow(false)
    val detailPanelVisible = _detailPanelVisible.asStateFlow()

    private val _listPanelVisible = MutableStateFlow(true)
    val listPanelVisible = _listPanelVisible.asStateFlow()

    private val _aircraftCount = MutableStateFlow(0)
    val aircraftCount = _aircraftCount.asStateFlow()

    private val _lastUpdateTime = MutableStateFlow<String?>(null)
    val lastUpdateTime = _lastUpdateTime.asStateFlow()

    private val _statusMessage = MutableStateFlow("Ready")
    val statusMessage = _statusMessage.asStateFlow()

    private val _detectedLocation = MutableStateFlow<DetectedLocation?>(null)
    val detectedLocation = _detectedLocation.asStateFlow()

    // --- API backoff ---
    private val _apiConsecutiveErrors = MutableStateFlow(0)
    val apiConsecutiveErrors = _apiConsecutiveErrors.asStateFlow()

    private val _apiInBackoff = MutableStateFlow(false)
    val apiInBackoff = _apiInBackoff.asStateFlow()

    // --- Aircraft photo ---
    private val _selectedAircraftPhoto = MutableStateFlow<ByteArray?>(null)
    val selectedAircraftPhoto = _selectedAircraftPhoto.asStateFlow()

    // --- Flight tracks ---
    private val _selectedFlightTracks = MutableStateFlow<List<FlightTrackPoint>>(emptyList())
    val selectedFlightTracks = _selectedFlightTracks.asStateFlow()

    // --- Bookmarks ---
    private val _bookmarks = MutableStateFlow<List<Bookmark>>(emptyList())
    val bookmarks = _bookmarks.asStateFlow()

    // --- Airport favorites ---
    private val _airportFavorites = MutableStateFlow<List<AirportFavorite>>(emptyList())
    val airportFavorites = _airportFavorites.asStateFlow()

    // --- Pending flight selection (after map move to remote flight) ---
    private val _pendingFlightCallsign = MutableStateFlow<String?>(null)
    val pendingFlightCallsign = _pendingFlightCallsign.asStateFlow()

    // --- Selected airport (for the airport info panel) ---
    private val _selectedAirport = MutableStateFlow<String?>(null)
    val selectedAirport = _selectedAirport.asStateFlow()

    // --- ATC audio stream (currently playing, or null) ---
    private val _atcStream = MutableStateFlow<AtcStreamInfo?>(null)
    val atcStream = _atcStream.asStateFlow()

    private val _atcStatus = MutableStateFlow<AtcStreamStatus?>(null)
    val atcStatus = _atcStatus.asStateFlow()

    // 0.0 .. 1.0 -- session-only (resets each launch).
    private val _atcVolume = MutableStateFlow(0.8)
    val atcVolume = _atcVolume.asStateFlow()

    // --- Immediate refresh signal (wakes monitoring loop early) ---
    private val _immediateRefreshSignal = MutableSharedFlow<Unit>(extraBufferCapacity = 1)
    val immediateRefreshSignal = _immediateRefreshSignal.asSharedFlow()

    // --- Mutators ---

    fun updateAircraftList(aircraft: List<AircraftData>) {
        _aircraftList.value = aircraft
        _aircraftCount.value = aircraft.size
    }

    fun selectFlight(callsign: String?) {
        _selectedCallsign.value = callsign
        _detailPanelVisible.value = callsign != null
        if (callsign != null) _selectedAirport.value = null
    }

    fun setTrackedFlight(callsign: String?) {
        _trackedFlightCallsign.value = callsign
    }

    fun cacheEnrichedData(callsign: String, data: EnrichedFlightData) {
        val existing = _cachedEnrichedData.value[callsign]
        val merged = if (existing != null) {
            EnrichedFlightData(
                origin = data.origin.ifEmpty { existing.origin },
                destination = data.destination.ifEmpty { existing.destination },
                aircraftType = data.aircraftType.ifEmpty { existing.aircraftType },
                registration = data.registration.ifEmpty { existing.registration },
                fr24Id = data.fr24Id.ifEmpty { existing.fr24Id },
                eta = data.eta.ifEmpty { existing.eta },
                departureTime = data.departureTime.ifEmpty { existing.departureTime },
                cachedAt = nowMs(),
            )
        } else data.copy(cachedAt = nowMs())
        _cachedEnrichedData.value = _cachedEnrichedData.value + (callsign to merged)
    }

    /**
     * Evict enrichment cache entries older than [maxAgeMs]. Default 2 hours --
     * covers most flights; after that, a hex code may belong to a different flight.
     */
    fun evictStaleEnrichmentData(maxAgeMs: Long = 2 * 60 * 60 * 1000L) {
        val now = nowMs()
        val filtered = _cachedEnrichedData.value.filterValues { now - it.cachedAt < maxAgeMs }
        if (filtered.size != _cachedEnrichedData.value.size) {
            _cachedEnrichedData.value = filtered
        }
    }

    fun updateSettings(settings: AppSettings) {
        _settings.value = settings
    }

    fun setPixooConnected(connected: Boolean) {
        _pixooConnected.value = connected
    }

    fun setPixooFrameIndex(index: Int) {
        _pixooFrameIndex.value = index
    }

    fun setPixooOriginalChannel(channel: Int?) {
        _pixooOriginalChannel.value = channel
    }

    fun setPixooStateSaved(saved: Boolean) {
        _pixooStateSaved.value = saved
    }

    fun setPixooCallsign(callsign: String?) {
        _pixooCallsign.value = callsign
    }

    fun setMonitoring(monitoring: Boolean) {
        _isMonitoring.value = monitoring
    }

    fun setDetailPanelVisible(visible: Boolean) {
        _detailPanelVisible.value = visible
    }

    fun setListPanelVisible(visible: Boolean) {
        _listPanelVisible.value = visible
    }

    fun setLastUpdateTime(time: String?) {
        _lastUpdateTime.value = time
    }

    fun setStatusMessage(message: String) {
        _statusMessage.value = message
    }

    fun setDetectedLocation(location: DetectedLocation?) {
        _detectedLocation.value = location
    }

    fun setApiConsecutiveErrors(count: Int) {
        _apiConsecutiveErrors.value = count
    }

    fun setApiInBackoff(inBackoff: Boolean) {
        _apiInBackoff.value = inBackoff
    }

    fun setSelectedAircraftPhoto(photo: ByteArray?) {
        _selectedAircraftPhoto.value = photo
    }

    fun setSelectedFlightTracks(tracks: List<FlightTrackPoint>) {
        _selectedFlightTracks.value = tracks
    }

    fun setPendingFlightCallsign(callsign: String?) {
        _pendingFlightCallsign.value = callsign
    }

    fun selectAirport(code: String?) {
        _selectedAirport.value = code
        if (code != null) {
            // Selecting an airport implicitly clears the flight selection.
            _selectedCallsign.value = null
            _detailPanelVisible.value = false
        }
    }

    fun setAtcStream(stream: AtcStreamInfo?) {
        _atcStream.value = stream
        if (stream == null) _atcStatus.value = null
    }

    fun setAtcStatus(status: AtcStreamStatus?) {
        _atcStatus.value = status
    }

    fun setAtcVolume(volume: Double) {
        _atcVolume.value = volume.coerceIn(0.0, 1.0)
    }

    fun requestImmediateRefresh() {
        _immediateRefreshSignal.tryEmit(Unit)
    }

    fun setBookmarks(bookmarks: List<Bookmark>) {
        _bookmarks.value = bookmarks
    }

    fun addBookmark(bookmark: Bookmark) {
        _bookmarks.value = _bookmarks.value + bookmark
    }

    fun removeBookmark(hex: String) {
        _bookmarks.value = _bookmarks.value.filter { it.hex != hex }
    }

    fun isBookmarked(hex: String): Boolean {
        return _bookmarks.value.any { it.hex == hex }
    }

    // --- Airport favorites ---
    fun setAirportFavorites(favorites: List<AirportFavorite>) {
        _airportFavorites.value = favorites
    }

    fun addAirportFavorite(favorite: AirportFavorite) {
        // De-dupe on code: if already favorited, leave as-is (preserve
        // the original timestamp so the list-order-by-timestamp is stable).
        val code = favorite.code.uppercase()
        if (_airportFavorites.value.any { it.code.equals(code, ignoreCase = true) }) return
        _airportFavorites.value = _airportFavorites.value + favorite.copy(code = code)
    }

    fun removeAirportFavorite(code: String) {
        _airportFavorites.value =
            _airportFavorites.value.filterNot { it.code.equals(code, ignoreCase = true) }
    }

    fun isAirportFavorite(code: String): Boolean =
        _airportFavorites.value.any { it.code.equals(code, ignoreCase = true) }

    private val shutdownHooks = mutableListOf<suspend () -> Unit>()

    fun addShutdownHook(hook: suspend () -> Unit) {
        shutdownHooks.add(hook)
    }

    suspend fun shutdown() {
        // Run shutdown hooks before cancelling scope
        shutdownHooks.forEach { it() }
        scope.cancel()
    }
}
